import { useEffect } from "react";

interface UseInfiniteScrollProps {
	fetchNextPage?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	isActive?: boolean;
	threshold?: number;
}

/**
 * Hook for implementing infinite scroll behavior.
 * Monitors scroll position and triggers fetchNextPage when user scrolls near bottom.
 *
 * @param fetchNextPage - Function to fetch the next page of data
 * @param hasNextPage - Whether there are more pages to fetch
 * @param isFetchingNextPage - Whether a page is currently being fetched
 * @param isActive - Whether the scroll listener should be active (useful for tabs)
 * @param threshold - Distance from bottom of page to trigger fetch (default: 500px)
 */
export function useInfiniteScroll({
	fetchNextPage,
	hasNextPage,
	isFetchingNextPage,
	isActive = true,
	threshold = 500,
}: UseInfiniteScrollProps) {
	useEffect(() => {
		if (!fetchNextPage || !isActive) return;

		let timeoutId: NodeJS.Timeout | null = null;
		let lastTriggerTime = 0;

		const handleScroll = () => {
			// Clear existing timeout
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			// Debounce scroll events
			timeoutId = setTimeout(() => {
				const now = Date.now();
				const scrollTop = window.scrollY;
				const windowHeight = window.innerHeight;
				const documentHeight = document.documentElement.scrollHeight;

				const isNearBottom =
					scrollTop + windowHeight >= documentHeight - threshold;

				// Add 1 second cooldown between triggers to prevent rapid calls
				if (
					isNearBottom &&
					hasNextPage &&
					!isFetchingNextPage &&
					now - lastTriggerTime > 1000
				) {
					lastTriggerTime = now;
					fetchNextPage();
				}
			}, 100); // 100ms debounce
		};

		// Use passive listener for better scroll performance
		window.addEventListener("scroll", handleScroll, { passive: true });

		// Check on mount in case content doesn't fill the viewport
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [fetchNextPage, hasNextPage, isFetchingNextPage, isActive, threshold]);
}
